/** @jsx h */
import {h, render, Fragment} from "preact";
import {onFind} from "@elements/init-modules-in-scope";
import {findIn, on, empty, removeAttribute, setAttribute} from '@elements/dom-utils';
import {translate} from '@elements/translations';
import {useState, useEffect} from 'preact/hooks';
import initModulesInScope, {cleanUpInScope} from "@elements/init-modules-in-scope";
import {assert, object, number, string, array, optional, enums, func, boolean, defaulted} from 'superstruct';
import {showNotification} from '@elements/alert-notification';
import {trackEvent} from '@elements/tracking/src/gtm';

const selectors = {
    base: '.js-supply-calculator',
    result: '.js-supply-calculator__result',
    loading: '.js-supply-calculator__loading',
    form: '.js-supply-calculator__form'
};

export function init() {
    onFind(selectors.base, function (Calculator) {
        let CalculatorForm = findIn(selectors.form, Calculator);
        let CalculatorLoading = findIn(selectors.loading, Calculator);

        const structConfig = object({
            ageGroups: array(object({
                text:string(),
                id:string(),
                value:string(),
            })),
            genders: array(object({
                text:string(),
                icon:string(),
                id:string(),
                value:string(),
            })),
            additionalInfos: array(object({
                text:string(),
                icon:string(),
                id:string(),
                value:string(),
            })),
            combinations: array(object({
                ageGroups:array(),
                genders:array(),
                additionalInfos:array(),
            }))
        })

        let url = Calculator.getAttribute('data-supply-calculator-data') || false;
        if(!url) {
            console.warn("supply-calculator: data-supply-calculator-data not set");
        }
        let request = fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        request.then(result => result.json()).then(result => {
            try {
                assert(result, structConfig);
                render(<RenderCalculator
                    handleSubmit={submitConfigurations}
                    {...result}
                />, CalculatorForm);
            } catch (error){
                for (const failure of error.failures()){
                    let errorText = `<div class="fz12 mb-4">This message is only displayed in dev environment</div><p class="fz16">Problem in JSON with Key: ${failure.key}</br>${failure.message}</p>`;

                    if (process.env.NODE_ENV === 'development') {
                        let CalculatorResult =  document.querySelector(selectors.result);
                        CalculatorResult.insertAdjacentHTML('afterbegin', `<div class="container--sm"><div class="alert alert-danger"><h3 class="mb-0">Configurator could not be loaded</h3>${errorText}</div></div>`);
                    }
                 }
            }

        }).catch(e => {
            console.log(e);
        });

        function submitConfigurations(ageGroup, gender, additionalInfo) {

            removeAttribute('hidden', CalculatorLoading);

            let configurationData = {
                ageGroup: ageGroup,
                gender: gender,
                additionalInfo: additionalInfo
            };

            let url = Calculator.getAttribute('data-supply-calculator-submit-url') || false;
            if(!url) {
                console.warn("supply-calculator: data-supply-calculator-submit-url not set");
            }

            let request = fetch(url, {
                method: 'POST',
                body: JSON.stringify(configurationData),
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            request.then(result => result.json()).then(result => {
                if(result.success) {
                    let CalculatorResult =  document.querySelector(selectors.result);
                    cleanUpInScope(CalculatorResult);
                    empty(CalculatorResult);
                    CalculatorResult.insertAdjacentHTML('afterbegin', result.html);
                    initModulesInScope(CalculatorResult);
                    setAttribute('hidden', 'hidden', CalculatorLoading);
                }
            }).catch(e => {
                setAttribute('hidden', 'hidden', CalculatorLoading);
                console.log(e);
            });
        }
    });
}

function RenderCalculator({
                              handleSubmit = noop(),
                              ageGroups = [],
                              additionalInfos = [],
                              genders = [],
                              combinations = []
                          }) {

    const [selectedAgeGroup, setSelectedAgeGroup] = useState(null);
    const [selectedGender, setSelectedGender] = useState(null);
    const [selectedAdditionalInfos, setSelectedAdditionalInfos] = useState(null);


    function availableAdditionalInfos() {
        let validCombinations = [];
        if(selectedAgeGroup && selectedGender) {
            combinations.map(function(item) {
                if(item.ageGroups.includes(selectedAgeGroup) && item.genders.includes(selectedGender)) {
                    validCombinations.push(...item.additionalInfos);
                }
            })
        }
        return validCombinations;
    }

    function isAvailableAdditionalInfo(id) {
        return availableAdditionalInfos().includes(id);
    }


    useEffect(function() {
        if(selectedAdditionalInfos && !isAvailableAdditionalInfo(selectedAdditionalInfos)) {
            setSelectedAdditionalInfos(null);
            console.log("reset additional infos");
        }
    }, [selectedGender, selectedAgeGroup])


    return (
        <div className="supply-calculator vertical-gutter gy-4 gy-md-5">
            <div className="supply-calculator__item vertical-gutter">
                <h3 className="supply-calculator__title">{translate('supply-calculator.Age')}</h3>

                <div className="supply-calculator__list row vertical-gutter gy-3 gx-3">
                    { ageGroups.map((item, key) => (
                        <div className="col-md-3 col-6">
                            <input name="ageGroup" type="radio" id={item.id}
                                   className="btn-check"
                                   value={item.value}
                                   onClick={(evt) => {
                                       setSelectedAgeGroup(evt.target.id);

                                       trackEvent(
                                           {
                                               category:"Bedarfsrechner",
                                               action:translate('supply-calculator.Age'),
                                               label: item.text
                                           }, {debug: true})
                                   }}/>
                            <label className="btn btn-check-grey w-100" htmlFor={item.id}>
                                { item.text }
                            </label>
                        </div>
                    ))}
                </div>
            </div>

            <div className={matchMedia('max-width:767px') ? 'vertical-gutter__item' : 'supply-calculator__item '}>
                <div className="row vertical-gutter gy-4">
                    <div className="col-md-6">
                        { matchMedia('max-width:767px') ? (
                            <div className="supply-calculator__item">
                                <h3 className="supply-calculator__title">{translate('supply-calculator.Gender')}</h3>
                            </div>
                        ) : (
                            <h3 className="supply-calculator__title">{translate('supply-calculator.Gender')}</h3>
                        )}

                        <div className="row g-3">
                            { genders.map((item, key) => (
                                <div className="col-6">
                                    <input className="btn-check"
                                        name="genders" type="radio" id={item.id} value={item.value}
                                           onClick={(evt) => {
                                               setSelectedGender(evt.target.id);
                                               trackEvent(
                                                   {
                                                       category:"Bedarfsrechner",
                                                       action: translate('supply-calculator.Gender'),
                                                       label: item.text
                                                   }, {debug: true})
                                           }}/>
                                    <label className="btn btn-check-grey w-100" htmlFor={item.id}>
                                        <span className={`icon icon-${item.icon}`} aria-hidden="true"/>
                                    </label>
                                    <div className="text-center">
                                        { item.text }
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={`col-md-6`}>
                        { matchMedia('max-width:767px') ? (
                            <div className="supply-calculator__item">
                                <h3 className={`supply-calculator__title ${!selectedAgeGroup || !selectedGender || availableAdditionalInfos().length === 0 ? 'is-disabled' : ''}`}>{translate('supply-calculator.I am...')}</h3>
                            </div>
                        ) : (
                            <h3 className={`supply-calculator__title ${!selectedAgeGroup || !selectedGender || availableAdditionalInfos().length === 0 ? 'is-disabled' : ''}`}>{translate('supply-calculator.I am...')}</h3>
                        )}

                        <div className="row vertical-gutter g-3 supply-calculator__part-content">
                            { additionalInfos.map((item, key) => (
                                <div className="col-6">
                                    <input name="additionalInfos" type="radio" id={item.id}
                                           value={item.value}
                                           className="btn-check"
                                           checked={item.id === selectedAdditionalInfos}
                                           disabled={!selectedAgeGroup || !selectedGender || !isAvailableAdditionalInfo(item.id)}
                                           onClick={(evt) => {
                                               setSelectedAdditionalInfos(evt.target.id);

                                               trackEvent(
                                                   {
                                                       category:"Bedarfsrechner",
                                                       action: translate('supply-calculator.I am...'),
                                                       label: item.text
                                                   }, {debug: true})
                                           }}/>
                                    <label className="btn btn-check-grey w-100" htmlFor={item.id}>
                                        <span className={`icon icon-${item.icon}`} aria-hidden="true"/>
                                    </label>
                                    <div className="btn-check-additional-label">
                                        { item.text }
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>


                <div className="supply-calculator__submit-wrapper">
                    <button
                        className="btn btn-outline--primary"
                        disabled={!selectedAgeGroup || !selectedGender || (!selectedAdditionalInfos && availableAdditionalInfos().length > 0)}
                        onClick={() => {
                            handleSubmit(selectedAgeGroup, selectedGender, selectedAdditionalInfos);

                            trackEvent(
                                {
                                    category:"Bedarfsrechner",
                                    action: "Berechnen",
                                    label: "Klick"
                                }, {debug: true})

                        }}>{translate('supply-calculator.Calculate Supply')}</button>
                </div>

            </div>
        </div>
    )
};


export function noop() {}

export function throwAlertMessage(type, title, errorMessage, closable) {
    showNotification({
        type: type,
        title: title,
        content: errorMessage,
        closable: closable
    });
}
